import { BulkUploadTab } from "@/components/horoscope/BulkUploadTab";
import { PaginationControls } from "@/components/pagination-controls";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useDeleteHoroscopeEntry,
  useHoroscopeEntries,
  useUpdateHoroscopeEntryStatus,
} from "@/hooks/useHoroscopeEntries";
import { useHoroscopeSigns } from "@/hooks/useHoroscopeSigns";
import { formatDate } from "@/lib/studio-constants";
import {
  Archive,
  ArchiveRestore,
  Calendar,
  CheckCircle2,
  Clock,
  Globe,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  Search,
  Send,
  Trash2,
  Upload,
  XCircle,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "wouter";

const STATUS_CONFIG: Record<
  string,
  {
    label: string;
    variant: "default" | "secondary" | "outline" | "destructive";
    icon: typeof Calendar;
  }
> = {
  draft: { label: "Draft", variant: "outline", icon: Calendar },
  submitted: { label: "Submitted", variant: "secondary", icon: Send },
  changes_requested: {
    label: "Changes Requested",
    variant: "secondary",
    icon: XCircle,
  },
  published: { label: "Published", variant: "default", icon: CheckCircle2 },
  archived: { label: "Archived", variant: "destructive", icon: Archive },
  outdated: { label: "Outdated", variant: "outline", icon: Clock },
};

const PAGE_SIZE = 5;

export function DailyEntriesTab() {
  const ALL_VALUE = "__all__";
  const [activeView, setActiveView] = useState<"list" | "bulk">("list");
  const [selectedSign, setSelectedSign] = useState<string>(ALL_VALUE);
  const [selectedStatus, setSelectedStatus] = useState<string>(ALL_VALUE);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [deleteEntryId, setDeleteEntryId] = useState<string | null>(null);

  const { data: signs = [] } = useHoroscopeSigns({ language: "en" });
  const {
    data: entriesData,
    isLoading,
    isError,
  } = useHoroscopeEntries({
    zodiacSign: selectedSign !== ALL_VALUE ? selectedSign : undefined,
    status: selectedStatus !== ALL_VALUE ? selectedStatus : undefined,
    limit: PAGE_SIZE,
    offset: (currentPage - 1) * PAGE_SIZE,
  });

  const updateStatusMutation = useUpdateHoroscopeEntryStatus();
  const deleteMutation = useDeleteHoroscopeEntry();

  useEffect(() => {
    setCurrentPage(1);
  }, [selectedSign, selectedStatus, searchQuery]);

  const entries = entriesData?.entries || [];
  const totalEntries = entriesData?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalEntries / PAGE_SIZE));
  const filteredEntries = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return entries;

    return entries.filter((entry) => {
      const combined = [
        entry.zodiacSign,
        entry.date,
        ...entry.translations.map((t) => t.title || ""),
        ...entry.translations.map((t) => t.readingText),
      ]
        .join(" ")
        .toLowerCase();

      return combined.includes(q);
    });
  }, [entries, searchQuery]);

  const handleStatusChange = (
    entryId: string,
    newStatus: "submitted" | "draft" | "archived",
  ) => {
    updateStatusMutation.mutate({
      id: entryId,
      data: { status: newStatus },
    });
  };

  const handleDelete = (id: string) => {
    setDeleteEntryId(id);
  };

  const confirmDelete = () => {
    if (deleteEntryId) {
      deleteMutation.mutate(deleteEntryId);
      setDeleteEntryId(null);
    }
  };

  if (isLoading) {
    return (
      <div className='flex items-center justify-center py-16'>
        <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
      </div>
    );
  }

  if (isError) {
    return (
      <div className='flex flex-col items-center justify-center py-16 text-center'>
        <Calendar className='h-8 w-8 text-muted-foreground mb-4' />
        <h3 className='text-lg font-medium'>Unable to load entries</h3>
        <p className='text-sm text-muted-foreground'>Please try again later.</p>
      </div>
    );
  }

  return (
    <>
      <div className='space-y-4'>
        <div className='flex flex-wrap items-center gap-3'>
          <Link href='/studio/horoscope/create'>
            <Button
              data-testid='button-create-entry'
              className='evo-gradient text-white hover:opacity-90'
            >
              <Plus className='h-4 w-4 mr-2' />
              Add Daily Entry
            </Button>
          </Link>
          <Button
            type='button'
            variant='outline'
            data-testid='button-toggle-bulk-upload'
            aria-pressed={activeView === "bulk"}
            className={
              activeView === "bulk"
                ? "evo-gradient text-white hover:opacity-90"
                : "bg-background"
            }
            onClick={() =>
              setActiveView((current) => (current === "bulk" ? "list" : "bulk"))
            }
          >
            <Upload className='h-4 w-4 mr-2' />
            Bulk Upload
          </Button>
        </div>

        {activeView === "bulk" ? (
          <BulkUploadTab />
        ) : (
          <>
            <Card>
              <CardContent className='p-4'>
                <div className='flex items-center justify-between gap-4'>
                  <div className='relative flex-1'>
                    <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                    <Input
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder='Search horoscope entries...'
                      className='pl-9'
                    />
                  </div>
                  <div className='flex items-center gap-3'>
                    <Select
                      value={selectedStatus}
                      onValueChange={setSelectedStatus}
                    >
                      <SelectTrigger className='w-[160px]'>
                        <SelectValue placeholder='All statuses' />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ALL_VALUE}>All Statuses</SelectItem>
                        <SelectItem value='draft'>Draft</SelectItem>
                        <SelectItem value='submitted'>Submitted</SelectItem>
                        <SelectItem value='changes_requested'>
                          Changes requested
                        </SelectItem>
                        <SelectItem value='published'>Published</SelectItem>
                        <SelectItem value='archived'>Archived</SelectItem>
                        <SelectItem value='outdated'>Outdated</SelectItem>
                      </SelectContent>
                    </Select>
                    <Select
                      value={selectedSign}
                      onValueChange={setSelectedSign}
                    >
                      <SelectTrigger className='w-[160px]'>
                        <SelectValue placeholder='All signs' />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value={ALL_VALUE}>All Signs</SelectItem>
                        {signs.map((sign) => (
                          <SelectItem key={sign.slug} value={sign.slug}>
                            {sign.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            <div className='text-sm text-muted-foreground mb-2'>
              {searchQuery
                ? `${filteredEntries.length} ${filteredEntries.length === 1 ? "entry" : "entries"} match search on this page`
                : `${totalEntries} ${totalEntries === 1 ? "entry" : "entries"} total`}
            </div>

            {filteredEntries.length === 0 && searchQuery ? (
              <div className='flex flex-col items-center justify-center py-16 text-center'>
                <Search className='h-8 w-8 text-muted-foreground mb-4' />
                <h3 className='text-lg font-medium'>No entries found</h3>
                <p className='text-sm text-muted-foreground'>
                  Try adjusting your search or filters
                </p>
              </div>
            ) : filteredEntries.length === 0 ? (
              <div className='flex flex-col items-center justify-center py-16 text-center'>
                <Calendar className='h-8 w-8 text-muted-foreground mb-4' />
                <h3 className='text-lg font-medium'>No entries found</h3>
                <p className='text-sm text-muted-foreground mb-4'>
                  Create your first horoscope entry to get started
                </p>
                <Link href='/studio/horoscope/create'>
                  <Button data-testid='button-create-first-entry'>
                    <Plus className='h-4 w-4 mr-2' />
                    Create Entry
                  </Button>
                </Link>
              </div>
            ) : (
              <Card>
                <CardContent className='p-0'>
                  <div className='overflow-x-auto'>
                    <div className='min-w-[900px]'>
                      <div className='grid grid-cols-[140px_120px_100px_1fr_140px_90px_140px_60px] px-4 py-3 text-sm text-muted-foreground border-b bg-muted/20'>
                        <span>Zodiac Sign</span>
                        <span>Date</span>
                        <span>Languages</span>
                        <span>Preview</span>
                        <span>Status</span>
                        <span>Version</span>
                        <span>Updated</span>
                        <span>Actions</span>
                      </div>
                      {filteredEntries.map((entry) => {
                        const statusCfg =
                          STATUS_CONFIG[entry.status] || STATUS_CONFIG.draft;
                        const translation =
                          entry.translations.find((t) => t.isDefault) ||
                          entry.translations[0];
                        const updatedLabel = formatDate(entry.updatedAt);
                        const entryDateLabel = formatDate(entry.date);

                        return (
                          <div
                            key={entry.id}
                            className='grid grid-cols-[140px_120px_100px_1fr_140px_90px_140px_60px] items-center px-4 py-4 border-b last:border-b-0 text-sm gap-3 hover:bg-muted/30 transition-colors'
                          >
                            <span className='font-medium capitalize'>
                              {entry.zodiacSign}
                            </span>
                            <span className='text-muted-foreground'>
                              {entryDateLabel}
                            </span>
                            <span className='inline-flex items-center gap-1.5 text-muted-foreground'>
                              <Globe className='h-3.5 w-3.5' />
                              {entry.translations.length}
                            </span>
                            <span className='text-muted-foreground truncate'>
                              {translation?.title ||
                                translation?.readingText ||
                                "--"}
                            </span>
                            <span>
                              <Badge
                                variant={statusCfg.variant}
                                className='text-xs'
                              >
                                {statusCfg.label}
                              </Badge>
                            </span>
                            <span className='text-muted-foreground'>
                              v{entry.version}
                            </span>
                            <span className='text-muted-foreground'>
                              {updatedLabel}
                            </span>
                            <div className='flex items-center justify-end'>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    size='icon'
                                    variant='ghost'
                                    className='h-8 w-8'
                                  >
                                    <MoreHorizontal className='h-4 w-4' />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent
                                  align='end'
                                  className='w-48'
                                >
                                  <DropdownMenuItem asChild>
                                    <Link
                                      href={`/studio/horoscope/edit/${entry.id}`}
                                    >
                                      <Pencil className='h-4 w-4 mr-2' />
                                      Edit
                                    </Link>
                                  </DropdownMenuItem>
                                  {(entry.status === "draft" ||
                                    entry.status === "changes_requested") && (
                                    <DropdownMenuItem
                                      onClick={() =>
                                        handleStatusChange(
                                          entry.id,
                                          "submitted",
                                        )
                                      }
                                    >
                                      <Send className='h-4 w-4 mr-2' />
                                      {entry.status === "changes_requested"
                                        ? "Resubmit for approval"
                                        : "Submit for approval"}
                                    </DropdownMenuItem>
                                  )}
                                  {(entry.status === "draft" ||
                                    entry.status === "submitted" ||
                                    entry.status === "changes_requested" ||
                                    entry.status === "published") && (
                                    <DropdownMenuItem
                                      onClick={() =>
                                        handleStatusChange(entry.id, "archived")
                                      }
                                    >
                                      <Archive className='h-4 w-4 mr-2' />
                                      Archive
                                    </DropdownMenuItem>
                                  )}
                                  {entry.status === "archived" && (
                                    <DropdownMenuItem
                                      onClick={() =>
                                        handleStatusChange(entry.id, "draft")
                                      }
                                    >
                                      <ArchiveRestore className='h-4 w-4 mr-2' />
                                      Restore to draft
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    className='text-destructive'
                                    onClick={() => handleDelete(entry.id)}
                                  >
                                    <Trash2 className='h-4 w-4 mr-2' />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  {totalEntries > 0 && (
                    <PaginationControls
                      currentPage={currentPage}
                      totalPages={totalPages}
                      totalItems={totalEntries}
                      startIndex={(currentPage - 1) * PAGE_SIZE}
                      endIndex={Math.min(currentPage * PAGE_SIZE, totalEntries)}
                      canGoNext={currentPage < totalPages}
                      canGoPrev={currentPage > 1}
                      onPageChange={setCurrentPage}
                      onNext={() =>
                        setCurrentPage((prev) => Math.min(totalPages, prev + 1))
                      }
                      onPrev={() =>
                        setCurrentPage((prev) => Math.max(1, prev - 1))
                      }
                    />
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      <AlertDialog
        open={!!deleteEntryId}
        onOpenChange={() => setDeleteEntryId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              horoscope entry.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
